import { PaginationState, SortingState } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useBrandConfig } from "src/brand/config";
import { FoldersListDataTable } from "src/core/components/folders-list/FoldersListDataTable";
import { PageBreadcrumb } from "src/core/components/layout/PageBreadcrumb";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/core/components/ui/tabs";
import { FolderListProvider, useFolderListContext } from "src/core/contexts/FolderListContext";
import { useFolderList } from "src/core/hooks/useFolderList";
import { useFolderNavigationStrategy } from "src/core/hooks/useFolderNavigationStrategy";
import { useStatusFilter } from "src/core/hooks/useStatusFilter";

const FoldersContent = () => {
  const { t } = useTranslation();

  const { hasAccessToStatus } = useStatusFilter();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const brandConfig = useBrandConfig();
  const { buildFolderPath } = useFolderNavigationStrategy();
  const { ownerUserIdFilter, followedByUserIdFilter, myFoldersFilter } = useFolderListContext();

  const {
    currentTab,
    setCurrentTab,
    columns,
    data: folders,
    loading,
    totalCount,
  } = useFolderList({ sorting, pagination });

  const filteredFolders = brandConfig.foldersConfig.filter((folder) =>
    hasAccessToStatus(folder.status)
  );

  // Réinitialiser toute la pagination quand on change d'onglet
  useEffect(() => {
    setPagination({
      pageIndex: 0,
      pageSize: 10,
    });
  }, [currentTab]);

  // Réinitialiser toute la pagination quand on change les filtres
  useEffect(() => {
    setPagination({
      pageIndex: 0,
      pageSize: 10,
    });
  }, [ownerUserIdFilter, followedByUserIdFilter, myFoldersFilter]);

  if (!currentTab || filteredFolders.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col">
      <PageBreadcrumb className="ml-40 mt-20 mb-5" />
      <Tabs value={currentTab} onValueChange={setCurrentTab} className="px-40">
        <TabsList className="folders-tab-list">
          {filteredFolders.map((folderConfig, index) => {
            return (
              <TabsTrigger key={index} value={folderConfig.status} className="folders-tab">
                <div className="folders-tab-title">{t(folderConfig.listKey)}</div>
              </TabsTrigger>
            );
          })}
        </TabsList>
        {filteredFolders.map((folder) => (
          <TabsContent key={folder.status} value={folder.status}>
            <FoldersListDataTable
              columns={columns}
              data={folders}
              icon={folder.icon}
              isLoading={loading}
              noResultText={folder.noResultText}
              sorting={sorting}
              onSortingChange={setSorting}
              status={folder.status}
              buildFolderPath={(row) => buildFolderPath({ id: row.id, status: row.status })}
              pagination={pagination}
              onPaginationChange={setPagination}
              totalCount={totalCount}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export const Folders = () => {
  return (
    <FolderListProvider>
      <FoldersContent />
    </FolderListProvider>
  );
};
